<!-- Main Layout Wrapper -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import Sidebar from '@/components/layout/Sidebar.vue'
import Header from '@/components/layout/Header.vue'
import ConnectionBanner from '@/components/layout/ConnectionBanner.vue'

const router = useRouter()
const route = useRoute()
const uiStore = useUiStore()
const authStore = useAuthStore()

const isAuthPage = computed(() => 
  route.matched.some(m => m.meta.guestOnly === true)
)

const showConnectionBanner = computed(() => 
  !isAuthPage.value && authStore.isAuthenticated
)

const mainClasses = computed(() => [
  'main',
  ...(!isAuthPage.value && !uiStore.sidebarCollapsed ? ['main--with-sidebar'] : []),
].join(' '))

const bannerHeight = ref(0)

function setBannerHeight(height: number) {
  bannerHeight.value = height
}
</script>

<template>
  <div class="layout">
    <!-- Connection Status Banner -->
    <ConnectionBanner v-if="showConnectionBanner" @height-change="setBannerHeight" />
    
    <!-- Sidebar -->
    <Sidebar v-if="!isAuthPage" />
    
    <!-- Main Content Area -->
    <div :class="mainClasses">
      <!-- Header -->
      <Header v-if="!isAuthPage" :banner-height="bannerHeight" />
      
      <!-- Page Content -->
      <main 
        :class="['page', { 'page--auth': isAuthPage }]" 
        :style="{ paddingTop: isAuthPage ? 0 : 'var(--header-height)' }"
        role="main"
        id="main-content"
      >
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: margin-left var(--transition-normal);
}

.main--with-sidebar {
  margin-left: var(--sidebar-width);
}

@media (max-width: 1023px) {
  .main--with-sidebar {
    margin-left: 0;
  }
}

.page {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.page--auth {
  justify-content: center;
  align-items: center;
  padding: var(--space-lg) var(--space-md);
  background: var(--color-bg-primary);
}

.page:not(.page--auth) {
  padding: var(--space-lg);
}

@media (max-width: 768px) {
  .page:not(.page--auth) {
    padding: var(--space-md);
  }
}

/* Page Transitions */
.page-enter-active,
.page-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>